'use client';
import classNames from 'classnames';
import Link from 'next/link';
import { ChangeEvent, FC, useActionState, useEffect, useState } from 'react';

import { useSession } from '@/entities/user';
import { registerAction } from '@/features/auth';
import { usePhoneMask } from '@/shared/lib';
import { Checkbox, Input } from '@/shared/ui';
import { MODAL_TYPES } from '@/widgets/modals/config/constants';
import { useModalStore } from '@/widgets/modals/model/store';

import styles from './signUp.module.scss';

interface SignUpProps {
  className?: string;
}

const initialState = {
  message: '',
  success: false,
  fieldErrors: {
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    password: '',
    password_confirmation: '',
    policy: '',
  },
};

const SignUp: FC<SignUpProps> = (props) => {
  const { className } = props;
  const { openModal } = useModalStore();
  const [state, formAction, isPending] = useActionState(
    registerAction,
    initialState,
  );
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    password: '',
    password_confirmation: '',
    policy: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const { phone, onChange } = usePhoneMask();
  const { refetch } = useSession();

  useEffect(() => {
    if (state.success) {
      refetch();
      openModal(MODAL_TYPES.emailConfirm);
    }
  }, [state, openModal, refetch]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSetCheckbox = (value: boolean) => {
    setForm((prev) => ({
      ...prev,
      policy: value,
    }));
  };

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.container__content}>
        <div className={styles.container__titleBlock}>
          <p className={styles.container__title}>Регистрация</p>
        </div>
        <form action={formAction} className={styles.container__form}>
          <div className={styles.container__form_item}>
            <Input
              className={styles.container__form_input}
              error={state?.fieldErrors?.first_name}
              label="Имя*"
              name="first_name"
              value={form.first_name}
              onChange={handleInputChange}
            />
            <Input
              className={styles.container__form_input}
              error={state?.fieldErrors?.last_name}
              label="Фамилия"
              name="last_name"
              value={form.last_name}
              onChange={handleInputChange}
            />
          </div>
          <div className={styles.container__form_item}>
            <Input
              className={styles.container__form_input}
              error={state?.fieldErrors?.email}
              label="E-mail*"
              name="email"
              value={form.email}
              onChange={handleInputChange}
            />
            <Input
              className={styles.container__form_input}
              error={state?.fieldErrors?.phone}
              label="Телефон"
              name="phone"
              type="tel"
              value={phone}
              onChange={onChange}
            />
          </div>
          <div
            className={classNames(
              styles.container__form_item,
              styles['container__form_item--last'],
            )}
          >
            <Input
              buttonType="button"
              className={styles.container__form_input}
              error={state?.fieldErrors?.password}
              label="Пароль"
              name="password"
              rightIcon={showPassword ? 'eyeOpen' : 'eyeClosed'}
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onButtonClick={toggleShowPassword}
              onChange={handleInputChange}
            />
            <Input
              buttonType="button"
              className={styles.container__form_input}
              error={state?.fieldErrors?.password_confirmation}
              label="Пароль"
              name="password_confirmation"
              rightIcon={showPassword ? 'eyeOpen' : 'eyeClosed'}
              type={showPassword ? 'text' : 'password'}
              value={form.password_confirmation}
              onButtonClick={toggleShowPassword}
              onChange={handleInputChange}
            />
          </div>
          <Checkbox
            checked={form.policy}
            className={styles.container__form_checkbox}
            error={state?.fieldErrors?.policy}
            name="policy"
            variant="white"
            onChange={handleSetCheckbox}
          >
            Согласие с{' '}
            <Link className={classNames('link link--underline')} href={''}>
              Политикой конфиденциальности
            </Link>
          </Checkbox>
          {state.message && <p role="alert">{state.message}</p>}
          <button
            className={classNames('button button--confirm-white')}
            disabled={isPending}
          >
            Продолжить
          </button>
        </form>
      </div>
      <div className={styles.container__bottom}>
        <button
          className={classNames('button button--confirm-black')}
          onClick={() => openModal(MODAL_TYPES.signIn)}
        >
          У меня есть аккаунт
        </button>
      </div>
    </div>
  );
};

export default SignUp;
